/**
 * Hook de controle de aceite versionado dos termos legais.
 * Persiste em localStorage com chave versionada — ao bumpar LEGAL_VERSION,
 * o médico precisa aceitar de novo na próxima sessão.
 *
 * Sincroniza entre abas via StorageEvent para consistência.
 */
import { useState, useEffect, useCallback } from 'react';
import { LEGAL_STORAGE_KEY } from '../utils/legalVersion';

export interface LegalConsentState {
  /** true se médico já aceitou a versão atual dos termos */
  accepted: boolean;
  /** Marcar aceito e persistir em localStorage (com timestamp de auditoria) */
  accept: () => void;
  /** Limpar aceite — uso em debug/testes/regressão */
  reset: () => void;
}

export function useLegalConsent(): LegalConsentState {
  // Inicializa lendo localStorage de forma segura (SSR-safe)
  const [accepted, setAccepted] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LEGAL_STORAGE_KEY) === 'true';
    } catch {
      // Ambiente sem localStorage (iframe sandboxed, etc.)
      return false;
    }
  });

  // Sincronização entre abas via storage event
  useEffect(() => {
    function handleStorage(e: StorageEvent) {
      if (e.key !== LEGAL_STORAGE_KEY) return;
      setAccepted(e.newValue === 'true');
    }
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const accept = useCallback(() => {
    try {
      localStorage.setItem(LEGAL_STORAGE_KEY, 'true');
      // Timestamp ISO 8601 para auditoria de conformidade LGPD/GDPR
      localStorage.setItem(
        `${LEGAL_STORAGE_KEY}-timestamp`,
        new Date().toISOString()
      );
    } catch {
      // Silencioso — se localStorage falhar, aceite persiste só em memória
    }
    setAccepted(true);
  }, []);

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(LEGAL_STORAGE_KEY);
      localStorage.removeItem(`${LEGAL_STORAGE_KEY}-timestamp`);
    } catch {
      // Silencioso
    }
    setAccepted(false);
  }, []);

  return { accepted, accept, reset };
}
